/**
 * UTM attribution capture.
 *
 * Captures `utm_*` params from the landing URL and persists them for the
 * browsing session (sessionStorage), so they survive client-side navigation
 * and can be attached to conversions (prayer submissions, email signups) and
 * sent to GA4 as event parameters for creative-level (utm_content) attribution.
 *
 * Follows the canonical convention in apps/americaprays/docs/UTM_CONVENTION.md.
 */

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "ap_utm_params";
const MAX_VALUE_LENGTH = 255;

/**
 * Reads utm_* params from a URL query string and, if any are present, persists
 * them for the session. Does NOT overwrite a previously-captured tagged session
 * with an untagged internal navigation (last tagged landing wins).
 */
export function captureUtmParams(search: string): void {
  if (typeof window === "undefined") return;

  try {
    const params = new URLSearchParams(search);
    const captured: UtmParams = {};

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) {
        captured[key] = value.slice(0, MAX_VALUE_LENGTH);
      }
    }

    if (Object.keys(captured).length > 0) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    }
  } catch {
    // sessionStorage can be unavailable (private mode / blocked); fail silently.
  }
}

/** Returns the UTM params captured this session, or an empty object. */
export function getStoredUtmParams(): UtmParams {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
}
